/**
 * Stocktake list-surface hooks (#1418). The list itself is now fetched by
 * `stocktakeResource` through the `ResourcePage` framework; the only hook the
 * `StocktakeListPage` wrapper still needs is the "open new stocktake"
 * mutation, moved here from the retired bespoke page so the feature owns its
 * own write path (no features → pages import). The count/review workflow
 * hooks stay in `pages/stocktake/hooks/useStocktake.ts` (those pages are
 * unchanged, #1392 non-goal).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { resourceKeys } from '@resource-kit/controllers';
import { queryKeys } from '@lib/queryKeys';
import { stocktakeAPI, type Stocktake, type OpenStocktakeInput } from '@/services';
import { stocktakeResource } from '../stocktakeResource';

// The converged list lives under the framework namespace (`['stocktakes', …]`);
// the count/review pages still read the legacy singular detail namespace
// (`['stocktake', …]`). Keyed off `stocktakeResource.slug` so the two can't
// drift.
const frameworkKeys = resourceKeys(stocktakeResource.host.slug);

export function useOpenStocktake() {
  const qc = useQueryClient();
  return useMutation<Stocktake, Error, OpenStocktakeInput>({
    mutationFn: (input) => stocktakeAPI.open(input),
    onSuccess: () => {
      // A new stocktake adds a row to the framework list — invalidate that
      // namespace so `/stocktake` shows it immediately (a plain `['stocktake']`
      // invalidation is a positional-tuple no-op against `['stocktakes', …]`).
      qc.invalidateQueries({ queryKey: frameworkKeys.all() });
      // Legacy singular namespace, still read by the count/review pages.
      qc.invalidateQueries({ queryKey: queryKeys.stocktake.all() });
    },
  });
}
